import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Brandmark } from "@/components/Brand";
import { useI18n } from "@/i18n";
import { Menu, X, MonitorSmartphone, Box, CalendarRange, ShieldCheck, Hammer, Eye, KeyRound, Check, Wrench, Sparkles, Phone, Gauge, Waves, Ruler, Camera, TrendingUp, Building2, Home } from "lucide-react";

const LANGS = ["EN", "DE", "ES"];
const LANG_NAMES = { EN: "English", DE: "Deutsch", ES: "Español" };

const IMG = {
  portal: "/services/portal-dashboard.png",
  twin: "/services/visualisation.jpg",
  calendar: "/services/staff-calendar.png",
};

const SERVICES_TICKER = [
  [ShieldCheck, "Scheduled inspections"],
  [KeyRound, "Keyholding & security"],
  [Wrench, "Maintenance & repairs"],
  [Hammer, "Trusted contractors"],
  [Sparkles, "Pre-arrival preparation"],
  [Phone, "Emergency response"],
  [Gauge, "Utility & bill oversight"],
  [Waves, "Pool & garden care"],
  [Building2, "Project management"],
  [Box, "Matterport digital twin"],
  [Ruler, "Measured floor plans"],
  [Eye, "CGI & virtual staging"],
  [Camera, "Photography & drone"],
  [MonitorSmartphone, "Client portal"],
  [CalendarRange, "Shared calendar"],
  [TrendingUp, "Rental management"],
  [Home, "Sales & going to market"],
];

function ServicesTicker() {
  const row = [...SERVICES_TICKER, ...SERVICES_TICKER];
  return (
    <div className="svc-ticker" data-testid="services-ticker" aria-label="Everything we do">
      <div className="svc-ticker-track">
        {row.map(([Icon, label], i) => (
          <span key={i} className="svc-ticker-item"><Icon size={16} strokeWidth={1.6} /> {label}</span>
        ))}
      </div>
    </div>
  );
}

const DIFFERENTIATORS = [
  {
    icon: MonitorSmartphone,
    label: "The client portal",
    title: "Your home, always in view.",
    body: "Every owner gets a private portal, opened from Mallorca or the other side of the world. A photographed report after each visit, a live timeline of everything we do, upcoming arrivals, invoices, documents and direct messaging with your named contact, no chasing, no wondering.",
    bullets: ["Photographed report after every visit", "Live timeline & recent activity", "Arrivals, invoices & documents in one place", "Message your contact directly"],
    img: IMG.portal,
    alt: "The Solvana client portal dashboard showing a property, latest visit and recent activity",
  },
  {
    icon: Box,
    label: "Matterport & visualisation",
    title: "Every home, captured in exceptional detail.",
    body: "This is where we go further than any traditional management company. Every property begins with a Matterport digital twin and measured floor plans, a permanent, walkable 3D record of layout, condition and contents. From there: CGI and virtual staging, before/after renders, architectural photography and drone, and white-label sales packs an agent can list with immediately.",
    bullets: ["Matterport 3D digital twin", "Measured floor plans", "CGI, virtual staging & before/after renders", "White-label sales packs for agents"],
    img: IMG.twin,
    alt: "3D digital twin dollhouse view of a Mallorcan villa",
  },
  {
    icon: CalendarRange,
    label: "Shared calendar & scheduling",
    title: "Every visit, arrival and report, in sync.",
    body: "Your visits, inspections and arrivals live in a shared calendar that syncs two-way with Google Calendar. Our team sees the same schedule you do, double-bookings are prevented automatically, and you can subscribe to your own property's calendar feed. The rhythm of your home, always up to date.",
    bullets: ["Two-way Google Calendar sync", "Scheduled visits & inspections", "Arrival & trip planning", "Your own private calendar feed"],
    img: IMG.calendar,
    alt: "The Solvana staff calendar with Google Calendar connection",
  },
];

const PILLARS = [
  {
    icon: ShieldCheck, label: "Protect", title: "Property care",
    intro: "The essentials, done impeccably, so nothing is ever overlooked.",
    items: [
      ["Scheduled inspections", "A full interior and exterior walk-through against a fixed checklist, leaks, damp and humidity, mould, storm damage, pests, security, appliances and water pressure, with a photo-documented report after every single visit."],
      ["Secure keyholding", "Your keys are stored in a coded key safe at our base, tagged by reference and never by address, with an access log recording every entry. Access and alarm codes are held securely, on a need-to-know basis, under a formal keyholding agreement."],
      ["Maintenance & contractors", "Vetted, insured local trades, plumber, electrician, pool, garden, air-conditioning, arranged, supervised and quality-checked, with a single point of contact and your approval before any spend."],
      ["Pre-arrival preparation", "Prepared to your checklist: heating or cooling on, shutters open, beds made, fridge stocked, pool and garden checked, so you walk into a home that's ready."],
      ["Emergency response", "A local number that answers day or night. We know your home's stopcock and systems and act fast on leaks, break-ins and storms."],
      ["Utility & bill oversight", "Meter readings tracked, anomalies flagged (a spike often means a leak), bills kept in order."],
    ],
  },
  {
    icon: Hammer, label: "Improve", title: "Project management",
    intro: "From a dripping tap to a full renovation, run end to end on your behalf.",
    items: [
      ["Renovation management", "End to end: scope, quotes, permits, scheduling, on-site supervision and budget control, we run the whole project so you don't have to."],
      ["In-house handyman", "Small jobs done quickly by our own team, without the wait or minimum call-out of a subcontractor."],
      ["Sourcing & interiors", "Furniture, finishes and fittings sourced, ordered, received and installed, coordinated with your designer where there is one."],
      ["Independent works oversight", "Regular site checks, progress photos and quality control on any trade or builder, on your behalf."],
    ],
  },
  {
    icon: Eye, label: "Present", title: "Visualisation",
    intro: "The part no one else does like we do, your home, documented and presented beautifully.",
    items: [
      ["Matterport digital twin & measured floor plans", "A full 3D scan you can walk from anywhere, the permanent, accurate record of your home's layout, condition and contents."],
      ["CGI & virtual staging", "Photorealistic renders of a room redesigned, refurnished or renovated, see the potential before a euro is spent."],
      ["Before / after renders", "Your property as it is, beside a CGI of what it could become, the most persuasive tool there is for planning or selling."],
      ["Photography & aerial drone", "Professional architectural photography and licensed drone footage through our trusted network."],
      ["White-label sales packs", "A complete marketing pack, photography, Matterport, floor plans and video, that an agent can list with immediately."],
    ],
  },
];

export default function Services() {
  const navigate = useNavigate();
  const { lang, setLang } = useI18n();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const els = Array.from(document.querySelectorAll("h2, .diff-row, [data-testid^='why-'], [data-testid^='pillar-']"));
    els.forEach((e) => e.classList.add("reveal"));
    const io = new IntersectionObserver((ents) => ents.forEach((en) => { if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); } }), { threshold: 0.12 });
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);

  const links = [
    { label: "Services", to: "/services" },
    { label: "Pricing", to: "/#pricing" },
    { label: "About Us", to: "/about" },
  ];

  return (
    <div>
      <nav style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(244,239,231,.94)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--line)" }}>
        <div className="wrapx" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 84 }}>
          <Link to="/" style={{ textDecoration: "none" }}><Brandmark tagline tagText="Property care · Mallorca" /></Link>
          <div className="nav-desk" style={{ display: "flex", gap: 34, fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted)" }}>
            {links.map((l) => <a key={l.label} href={l.to} className="navlink" style={{ textDecoration: "none", color: "inherit" }}>{l.label}</a>)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            <div style={{ position: "relative" }}>
              <button onClick={() => setLangOpen((o) => !o)} data-testid="lang-toggle" style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid var(--line)", padding: "7px 11px", cursor: "pointer", fontSize: 11, letterSpacing: ".14em", color: "var(--ink)", textTransform: "uppercase" }}>
                {lang.toUpperCase()}<span style={{ fontSize: 9, color: "var(--honey)", transform: langOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}>▾</span>
              </button>
              {langOpen && <div onClick={() => setLangOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 55 }} />}
              {langOpen && (
                <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "var(--soft)", border: "1px solid var(--line)", boxShadow: "0 14px 34px -20px rgba(33,29,24,.5)", zIndex: 60, minWidth: 130 }}>
                  {LANGS.map((l) => (
                    <button key={l} onClick={() => { setLang(l.toLowerCase()); setLangOpen(false); }} data-testid={`lang-${l.toLowerCase()}`} style={{ display: "block", width: "100%", textAlign: "left", background: lang === l.toLowerCase() ? "rgba(176,141,87,.12)" : "none", border: "none", padding: "10px 14px", cursor: "pointer", fontSize: 12, letterSpacing: ".08em", color: lang === l.toLowerCase() ? "var(--ink)" : "var(--muted)" }}>{LANG_NAMES[l]}</button>
                  ))}
                </div>
              )}
            </div>
            <button className="navlink nav-hide-mobile" onClick={() => navigate("/login")} style={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted)", background: "none", border: "none", cursor: "pointer" }} data-testid="client-login-nav">Client login</button>
            <a href="/#book" className="btn-ink nav-hide-mobile" style={{ textDecoration: "none" }} data-testid="book-call-nav">Book a call</a>
            <button className="nav-burger" onClick={() => setMenuOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink)", padding: 4 }}><Menu size={24} /></button>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 80, background: "var(--paper)", padding: "26px clamp(24px,6vw,40px)", display: "flex", flexDirection: "column" }} data-testid="mobile-menu">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 36 }}>
            <Brandmark fontSize={20} size={28} />
            <button onClick={() => setMenuOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink)" }}><X size={26} /></button>
          </div>
          <nav style={{ display: "flex", flexDirection: "column" }}>
            {links.map((l) => (
              <a key={l.label} href={l.to} onClick={() => setMenuOpen(false)} className="font-serif-c" style={{ textDecoration: "none", borderBottom: "1px solid var(--line)", padding: "18px 0", fontSize: 30, fontWeight: 300, color: "var(--ink)" }}>{l.label}</a>
            ))}
            <button onClick={() => { setMenuOpen(false); navigate("/login"); }} className="font-serif-c" style={{ background: "none", border: "none", borderBottom: "1px solid var(--line)", textAlign: "left", padding: "18px 0", fontSize: 30, fontWeight: 300, color: "var(--ink)", cursor: "pointer" }}>Client login</button>
          </nav>
          <a href="/#book" onClick={() => setMenuOpen(false)} className="btn-ink btn-olive" style={{ marginTop: 32, textDecoration: "none", textAlign: "center" }}>Book a call</a>
        </div>
      )}

      {/* HERO */}
      <ServicesTicker />

      <header className="wrapx" style={{ padding: "clamp(56px,8vw,110px) clamp(24px,5vw,52px) clamp(36px,4vw,52px)" }}>
        <div className="fade-up" style={{ maxWidth: 900 }}>
          <span className="lbl">What we do, and why it's different</span>
          <h1 style={{ fontSize: "clamp(40px,6.4vw,82px)", margin: "24px 0 0", letterSpacing: "-.01em" }}>Everything your home needs, and something no one else gives you.</h1>
          <p style={{ fontSize: 19, color: "var(--muted)", maxWidth: "62ch", margin: "30px 0 0", lineHeight: 1.75 }}>
            We look after Mallorca's finest homes the way you'd look after them yourself, inspections, maintenance, renovations and arrivals, all handled by one trusted team. But where most companies stop at the occasional text message, we go further: a private portal, a Matterport digital twin of your home, and a shared calendar. So wherever you are in the world, you can <i>see</i> exactly how your home is being cared for.
          </p>
        </div>
      </header>

      {/* WHY OWNERS CHOOSE US */}
      <section className="wrapx" style={{ paddingBottom: "clamp(8px,2vw,20px)" }}>
        <div className="why-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "clamp(24px,4vw,52px)", borderTop: "1px solid var(--line)", paddingTop: "clamp(32px,4vw,48px)" }}>
          {[
            ["You see everything, from anywhere", "Your portal shows every visit, photographed report, invoice and message in real time, no chasing, no wondering."],
            ["One accurate record of your home", "A Matterport digital twin and measured floor plans that never go out of date, guiding every repair, renovation and future sale."],
            ["One person who knows your home", "Not a call centre or a franchise. A named contact who answers the phone and treats your home as their own."],
          ].map(([h, p], i) => (
            <div key={i} data-testid={`why-${i}`}>
              <div className="font-serif-c" style={{ fontSize: 22, color: "var(--honey)" }}>{String(i + 1).padStart(2, "0")}</div>
              <h3 style={{ fontSize: "clamp(19px,1.6vw,22px)", margin: "12px 0 10px", fontWeight: 500 }}>{h}</h3>
              <p style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.7 }}>{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="sec-pad">
        <div className="wrapx">
          <div style={{ maxWidth: 660, marginBottom: "clamp(44px,6vw,72px)" }}>
            <span className="lbl">A closer look</span>
            <h2 style={{ fontSize: "clamp(32px,4.5vw,56px)", marginTop: 16 }}>The three tools that keep you in control.</h2>
          </div>
          <div style={{ display: "grid", gap: "clamp(56px,8vw,120px)" }}>
            {DIFFERENTIATORS.map((d, i) => (
              <div key={d.label} className="diff-row" data-testid={`differentiator-${i}`} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "clamp(32px,5vw,72px)", alignItems: "center" }}>
                <div style={{ order: i % 2 === 1 ? 2 : 1 }}>
                  <div className={`icon-badge icon-diff icon-diff-${i}`} style={{ width: 54, height: 54, borderRadius: "50%", border: "1px solid var(--line)", display: "grid", placeItems: "center", marginBottom: 22 }}>
                    <d.icon size={24} strokeWidth={1.4} color="var(--honey)" />
                  </div>
                  <span className="lbl" style={{ color: "var(--honey)" }}>{d.label}</span>
                  <h3 style={{ fontSize: "clamp(28px,3.4vw,44px)", margin: "12px 0 18px", fontWeight: 400 }}>{d.title}</h3>
                  <p style={{ color: "var(--muted)", fontSize: 16.5, lineHeight: 1.8, marginBottom: 24 }}>{d.body}</p>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
                    {d.bullets.map((b) => (
                      <li key={b} style={{ display: "flex", alignItems: "flex-start", gap: 12, fontSize: 15.5, color: "var(--ink)" }}>
                        <Check size={17} strokeWidth={2} color="var(--honey)" style={{ marginTop: 3, flexShrink: 0 }} />{b}
                      </li>
                    ))}
                  </ul>
                </div>
                <div style={{ order: i % 2 === 1 ? 1 : 2 }}>
                  <div style={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 40px 80px -40px rgba(33,29,24,.45)", border: "1px solid var(--line)", background: "var(--soft)" }}>
                    <img src={d.img} alt={d.alt} style={{ width: "100%", display: "block" }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS IN FULL */}
      <section className="sec-pad" style={{ background: "var(--ink)", color: "var(--paper)" }}>
        <div className="wrapx">
          <div style={{ maxWidth: 640, marginBottom: "clamp(44px,6vw,72px)" }}>
            <span className="lbl" style={{ color: "var(--sand)" }}>Everything included</span>
            <h2 style={{ fontSize: "clamp(32px,4.5vw,56px)", color: "var(--soft)", marginTop: 16 }}>The full detail, nothing hidden.</h2>
          </div>
          <div className="pillars-full" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "clamp(32px,4vw,60px)" }}>
            {PILLARS.map((p) => (
              <div key={p.label} data-testid={`pillar-${p.label.toLowerCase()}`} style={{ borderTop: "1px solid rgba(244,239,231,.18)", paddingTop: 28 }}>
                <div className={`icon-badge icon-${p.label.toLowerCase()}`} style={{ width: 50, height: 50, borderRadius: "50%", border: "1px solid rgba(244,239,231,.22)", display: "grid", placeItems: "center", marginBottom: 20 }}>
                  <p.icon size={22} strokeWidth={1.4} color="var(--honey)" />
                </div>
                <div className="lbl" style={{ color: "var(--sand)", letterSpacing: ".28em" }}>{p.label}</div>
                <h3 style={{ fontSize: "clamp(26px,2.6vw,34px)", margin: "12px 0 10px", color: "var(--soft)", fontWeight: 400 }}>{p.title}</h3>
                <p style={{ color: "#c3bcaf", fontSize: 15.5, lineHeight: 1.7, marginBottom: 26 }}>{p.intro}</p>
                <div style={{ display: "grid", gap: 20 }}>
                  {p.items.map(([t, d]) => (
                    <div key={t}>
                      <b style={{ color: "var(--soft)", fontWeight: 500, fontSize: 15.5, display: "block", marginBottom: 5 }}>{t}</b>
                      <span style={{ color: "#a89f90", fontSize: 14, lineHeight: 1.7 }}>{d}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KEYHOLDING */}
      <section className="sec-pad">
        <div className="wrapx keyhold-grid" style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "clamp(24px,4vw,56px)", alignItems: "start", maxWidth: 1000 }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", border: "1px solid var(--line)", display: "grid", placeItems: "center", flexShrink: 0 }}>
            <KeyRound size={24} strokeWidth={1.4} color="var(--honey)" />
          </div>
          <div>
            <span className="lbl">Keyholding &amp; security</span>
            <h2 style={{ fontSize: "clamp(28px,3.6vw,46px)", margin: "14px 0 18px", fontWeight: 400 }}>Your keys, held the way you'd want them held.</h2>
            <p style={{ color: "var(--muted)", fontSize: 16.5, lineHeight: 1.8, maxWidth: "64ch" }} data-testid="keyholding-callout">Your keys are held in a coded key safe at our base, tagged by reference, never by address, so a lost or stolen key can never be matched to your home. Every entry is logged: which property, who, in and out. Your access and alarm codes are held securely and on a need-to-know basis. It's all underpinned by a formal keyholding agreement and care-custody insurance, cover specifically for the keys and property in our care. Quiet, disciplined security, so you never have to think about it.</p>
            <p style={{ marginTop: 22, fontSize: 13.5, color: "var(--muted)", maxWidth: "64ch", lineHeight: 1.75 }} data-testid="insurance-line">Fully insured, including professional indemnity and cover for the keys and property in our care. Clear, transparent pricing, and your approval before any spend. After every visit, a photographed report, so you always know the condition of your home.</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="sec-pad" style={{ borderTop: "1px solid var(--line)" }}>
        <div className="wrapx">
          <div style={{ marginBottom: "clamp(40px,5vw,64px)" }}><span className="lbl">How it works</span><h2 style={{ fontSize: "clamp(32px,4.5vw,56px)", marginTop: 16 }}>Four quiet steps.</h2></div>
          <div className="how-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "clamp(24px,3vw,44px)" }}>
            {[["I", "A call", "We learn your home, your worries and how often you're here."], ["II", "Onboard", "A Matterport scan, measured floor plans and your digital property profile, and we securely collect your access codes, manuals and preferred suppliers so we can act quickly."], ["III", "The rhythm", "Scheduled inspections, with a photographed report to your phone after every visit."], ["IV", "When needed", "Repairs, renovations and arrivals, handled by one trusted contact."]].map(([n, h, p]) => (
              <div key={n} style={{ borderTop: "1px solid var(--ink)", paddingTop: 22 }}>
                <div className="font-serif-c" style={{ fontSize: 22, color: "var(--honey)" }}>{n}</div>
                <h4 style={{ fontSize: 24, margin: "12px 0 8px", fontWeight: 400 }}>{h}</h4>
                <p style={{ color: "var(--muted)", fontSize: 14.5, lineHeight: 1.7 }}>{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ borderTop: "1px solid var(--line)", padding: "clamp(72px,10vw,140px) 0", textAlign: "center" }}><div className="wrapx" style={{ maxWidth: 680 }}>
        <span className="lbl">Say hello</span>
        <h2 style={{ fontSize: "clamp(38px,5.5vw,68px)", margin: "16px 0 22px" }}>See it for yourself.</h2>
        <p style={{ color: "var(--muted)", maxWidth: "46ch", margin: "0 auto 34px", fontSize: 17 }}>A twenty-minute private call, no obligation. We'll walk you through the portal, a digital twin and exactly how we'd look after your home.</p>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="/#book" className="btn-ink btn-olive" style={{ textDecoration: "none" }} data-testid="services-book-btn">Book a call</a>
          <a href="/sample-report" className="btn-ghost" style={{ textDecoration: "none", padding: "13px 26px", border: "1px solid var(--ink)", color: "var(--ink)" }} data-testid="services-sample-btn">See a sample report</a>
          <a href="/#pricing" className="btn-ghost" style={{ textDecoration: "none", padding: "13px 26px", border: "1px solid var(--ink)", color: "var(--ink)" }} data-testid="services-pricing-btn">See pricing</a>
        </div>
      </div></section>

      <footer style={{ background: "var(--ink)", color: "#a89f90", padding: "clamp(48px,6vw,72px) 0", fontSize: 13.5 }}>
        <div className="wrapx" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
          <Brandmark light fontSize={26} tagline />
          <div style={{ display: "flex", gap: 22, fontSize: 11, letterSpacing: ".08em" }}>
            <Link to="/" style={{ color: "#a89f90", textDecoration: "none" }}>HOME</Link>
            <Link to="/about" style={{ color: "#a89f90", textDecoration: "none" }}>ABOUT</Link>
            <a href="/#pricing" style={{ color: "#a89f90", textDecoration: "none" }}>PRICING</a>
            <Link to="/login" style={{ color: "#a89f90", textDecoration: "none" }}>CLIENT LOGIN</Link>
          </div>
        </div>
        <div className="wrapx" style={{ marginTop: 24, fontSize: 11, letterSpacing: ".08em", color: "#8a8072" }}>© 2026 Solvana Group · Fully insured · Mallorca · Balearic Islands</div>
      </footer>

      <style>{`.navlink{background:none;border:none;cursor:pointer;transition:color .3s}.navlink:hover{color:var(--ink)!important}.nav-burger{display:none;align-items:center}@media(max-width:960px){.nav-desk{display:none!important}.nav-burger{display:inline-flex!important}}@media(max-width:760px){.nav-hide-mobile{display:none!important}}@media(max-width:900px){.diff-row{grid-template-columns:1fr!important}.diff-row>div:first-child{order:1!important}.diff-row>div:last-child{order:2!important}.pillars-full{grid-template-columns:1fr!important;gap:44px!important}}@media(max-width:760px){.how-grid{grid-template-columns:1fr 1fr!important}.keyhold-grid{grid-template-columns:1fr!important}.why-grid{grid-template-columns:1fr!important;gap:32px!important}}`}</style>
    </div>
  );
}
